/**
 * @fileoverview System-web lookup category and value management page.
 * @module system-web/pages/LookupsPage
 *
 * Responsibilities:
 * - Render and edit system lookup categories, groups, and values.
 * - Coordinate local-first Dexie reads/writes and tenant lookup propagation.
 * - Provide the standardized grid, templates, filtering, and slideout experience.
 *
 * Dependencies: Dexie-backed lookup services, shared ui-admin controls, and
 *   the synced module catalog from moduleService.
 * Architecture: Local-first system administration; writes go through lookupService
 *   and synchronize in the background to tenant databases.
 * Notes: Module selectors use listModuleOptions() from the real synced catalog,
 *   never a hardcoded module list.
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';
import { fetchAuditLogs } from '../services/systemAuditLogService';
import { X, Eye, Pencil, Trash2, EyeOff, Plus, LayoutGrid, RefreshCw, Save, Share2, Star, ExternalLink, MoreVertical, AlertTriangle, ChevronDown, ChevronRight, FolderPlus, Copy, GripVertical, Download, ToggleLeft, Send } from 'lucide-react';
import { DataGrid, } from '@ui-admin/components/DataGrid';
import { GridTemplatesModal } from '@ui-admin/components/GridPageShell';
import { ConfirmDeleteModal, ConfirmBulkDeleteModal } from '@ui-admin/components/ui';
import type { BulkDeleteRecord } from '@ui-admin/components/ui/ConfirmBulkDeleteModal';
import type { DataGridColumn, DataGridRowAction, GridTemplate, GridState } from '@ui-admin/components/DataGrid';
import {
  useGridPage,
  loadTemplates,
} from '@ui-admin/components/GridPageShell';
import { useAuth } from '../auth/AuthProvider';
import { useSystemDb } from '../components/SystemDBProvider';
import { IconPicker, getIconComponent } from '@ui-admin/components/IconPicker';
import { LookupValueModal as SharedLookupValueModal } from '@ui-admin/components/lookups/LookupValueModal';
import {
  listLookupCategories,
  listLookupValues,
  saveLookupCategory,
  saveLookupValue,
  setLookupHidden,
  softDeleteLookupCategory,
  softDeleteLookupValue,
  listLookupGroups,
  saveLookupGroup,
  deleteLookupGroup,
  bulkAssignValuesToGroup,
  reorderLookupValue,
  syncLookupsToAllTenants,
  type LookupCategory,
  type LookupScope,
  type LookupValue,
  type LookupGroup,
  type LookupGroupInfo,
  type InheritancePolicy,
  type LookupEditPolicy,
  type LookupDisplay,
} from '../services/lookupService';
import { listModuleOptions } from '../services/moduleService';
import { useFeatureTier } from '../contexts/FeatureTierProvider';
import { DndContext, DragOverlay, useDroppable, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import {
  LOOKUP_ENTITY_CATEGORIES,
  DEFAULT_LOOKUP_SCOPE_POLICY,
  normalizeLookupEntityCategories,
} from '@firesquirrel/core-domain';

// ============================================================
// Types
// ============================================================

type LookupValueRow = LookupValue & {
  canEdit: boolean;
  canHide: boolean;
  canDelete: boolean;
  canManage: boolean; // true when the category is manageable — allows DnD group reassignment even for seed values
};

// ============================================================
// Constants
// ============================================================

const GRID_ID = 'lookups';
const SCOPE_OPTIONS: LookupScope[] = ['system', 'organization', 'tenant'];

const SCOPE_BADGE_COLORS: Record<LookupScope, { bg: string; text: string }> = {
  system: { bg: 'bg-blue-100', text: 'text-blue-700' },
  organization: { bg: 'bg-purple-100', text: 'text-purple-700' },
  tenant: { bg: 'bg-green-100', text: 'text-green-700' },
};

function labelScope(s: LookupScope) {
  return s === 'system' ? 'System' : s === 'organization' ? 'Organization' : 'Tenant';
}

function getCategoryModules(module: LookupCategory['module']): string[] {
  if (Array.isArray(module)) return module;
  return module ? [module] : [];
}

const STRUCTURED_CATEGORY_LOOKUP_TYPES = new Set(['caseType', 'eventType', 'inspectionType']);

const LOOKUP_SCOPE_POLICY_BY_TYPE: Record<string, { scopeMode: 'all' | 'entity' | 'module' | 'entityAndModule'; enforceCategory: boolean; enforceModule: boolean }> = {
  caseType: { scopeMode: 'entityAndModule', enforceCategory: true, enforceModule: true },
  inspectionType: { scopeMode: 'entity', enforceCategory: true, enforceModule: false },
  organizationType: DEFAULT_LOOKUP_SCOPE_POLICY,
};

function getLookupScopePolicy(lookupType: string) {
  return LOOKUP_SCOPE_POLICY_BY_TYPE[lookupType] ?? DEFAULT_LOOKUP_SCOPE_POLICY;
}

function deriveScopePolicy(scopeMode: 'all' | 'entity' | 'module' | 'entityAndModule') {
  return {
    scopeMode,
    enforceCategory: scopeMode === 'entity' || scopeMode === 'entityAndModule',
    enforceModule: scopeMode === 'module' || scopeMode === 'entityAndModule',
  };
}

function normalizeSelectedModules(modules: unknown): string[] {
  if (typeof modules === 'string') {
    const trimmed = modules.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(modules)) {
    return Array.from(new Set(modules.map((item) => typeof item === 'string' ? item.trim() : '').filter(Boolean)));
  }
  return [];
}

function ModuleMultiSelectDropdown({
  options,
  selected,
  onChange,
  label = 'Modules',
  emptyText = 'No modules available',
}: {
  options: { key: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  label?: string;
  emptyText?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedLabels = selected
    .map((key) => options.find((option) => option.key === key)?.label || key)
    .filter(Boolean);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full border rounded-md px-3 py-2 text-sm bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
      >
        <span className="truncate text-gray-700">{selectedLabels.length ? selectedLabels.join(', ') : `${label}: unrestricted`}</span>
        <ChevronDown size={16} className={`shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow-lg">
          <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 text-[11px] text-gray-500">
            <button type="button" onClick={() => onChange([])} className="hover:text-orange-600">Clear all / unrestricted</button>
            <button type="button" onClick={() => onChange(options.map((option) => option.key))} className="hover:text-orange-600" disabled={options.length === 0}>Select all</button>
          </div>
          <div className="max-h-48 overflow-auto p-3 space-y-2">
            {options.length > 0 ? options.map((option) => {
              const checked = selected.includes(option.key);
              return (
                <label key={option.key} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onChange(checked ? selected.filter((item) => item !== option.key) : [...selected, option.key])}
                    className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                  />
                  <span>{option.label}</span>
                </label>
              );
            }) : <div className="text-sm text-gray-400">{emptyText}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Seed templates for the Lookups grid
// ============================================================

const SEED_TEMPLATES: GridTemplate[] = [
  {
    _id: 'lookups-default-template',
    gridType: 'lookups',
    name: 'Default View',
    description: 'Standard lookups layout with all categories.',
    state: { columnVisibility: {}, columnOrder: [], sorting: [], filterValues: {}, pageSize: 25, searchText: '' },
    scope: 'system',
    createdBy: { userId: '507f1f77bcf86cd799439001', displayName: 'System' },
    isDefault: true, isPinned: false, usageCount: 0,
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
  {
    _id: 'lookups-inspection-template',
    gridType: 'lookups',
    name: 'Inspection Lookups',
    description: 'Filter to show only inspection-related lookup categories.',
    state: { columnVisibility: {}, columnOrder: [], sorting: [{ id: 'displayName', desc: false }], filterValues: {}, pageSize: 25, searchText: 'inspection' },
    scope: 'shared',
    createdBy: { userId: '507f1f77bcf86cd799439002', displayName: 'Fire Marshal Davis' },
    isDefault: false, isPinned: false, usageCount: 0,
    createdAt: '2026-01-01T00:00:00Z', updatedAt: '2026-01-01T00:00:00Z',
  },
];

// ============================================================
// Helpers
// ============================================================

function buildPermissions(value: LookupValue, scope: LookupScope): Pick<LookupValueRow, 'canEdit' | 'canHide' | 'canDelete'> {
  const owned = value.originScope === scope;
  const inherited = !owned;
  // System scope can edit all system-owned values regardless of inheritance policy
  const canEdit = (scope === 'system' && owned)
    || (owned && value.inheritancePolicy === 'custom_owned')
    || (inherited && scope === 'organization' && Boolean(value.editPolicy?.orgCanEdit))
    || (inherited && scope === 'tenant' && Boolean(value.editPolicy?.tenantCanEdit));
  const canDelete = (scope === 'system' && owned) || (owned && value.inheritancePolicy === 'custom_owned');
  let canHide = false;
  if (inherited && value.inheritancePolicy === 'seed_hideable') canHide = true;
  else if (inherited && scope === 'tenant' && (Boolean(value.editPolicy?.tenantCanHide) || Boolean(value.tenantCanHideInherited))) canHide = true;
  else if (inherited && scope === 'organization' && (Boolean(value.editPolicy?.orgCanHide) || Boolean(value.orgCanHideInherited))) canHide = true;
  return { canEdit, canHide, canDelete };
}

// ============================================================
// Value Row (draggable, supports multi-select)
// ============================================================

function ValueRow({ v, indented, selected, multiSelectMode, onSelect, onDoubleClick, onEdit, onHide, onDelete }: {
  v: LookupValueRow;
  indented?: boolean;
  selected?: boolean;
  multiSelectMode?: boolean;
  onSelect?: (id: string, multi: boolean) => void;
  onDoubleClick?: (id: string) => void;
  onEdit: () => void;
  onHide: () => void;
  onDelete: () => void;
}) {
  const isDraggable = v.canManage && !multiSelectMode;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: v.id,
    disabled: !isDraggable,
    data: { type: 'value', value: v, valueId: v.id },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.4 : 1,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, transition }}
      className={`flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 hover:bg-gray-50 transition-colors text-sm ${indented ? 'pl-8' : ''} ${selected ? 'bg-orange-50 border-orange-200' : ''} ${isOver && !isDragging ? 'border-t-2 border-orange-400' : ''}`}
      onClick={() => { if (multiSelectMode && onSelect) onSelect(v.id, false); }}
      onDoubleClick={() => { if (!multiSelectMode && v.canManage && onDoubleClick) onDoubleClick(v.id); }}
    >
      {/* Drag handle — hidden in multi-select mode */}
      {!multiSelectMode && isDraggable ? (
        <button {...listeners} {...attributes} className="p-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing shrink-0" title="Drag to reorder">
          <GripVertical size={14} />
        </button>
      ) : !multiSelectMode ? (
        <div className="w-[18px] shrink-0" />
      ) : null}
      {/* Multi-select checkbox — only shown in multi-select mode */}
      {multiSelectMode && v.canManage ? (
        <input type="checkbox" checked={!!selected} title="Select value"
          onChange={(e) => { e.stopPropagation(); if (onSelect) onSelect(v.id, e.nativeEvent instanceof MouseEvent && e.nativeEvent.shiftKey); }}
          className="w-3.5 h-3.5 text-orange-600 rounded focus:ring-orange-500 shrink-0 cursor-pointer" />
      ) : multiSelectMode ? (
        <div className="w-3.5 shrink-0" />
      ) : null}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{v.name}</div>
        <div className="text-xs text-gray-400 truncate">{v.code}</div>
      </div>
      <div className="text-xs text-gray-500 shrink-0 w-14">
        {v.inheritancePolicy === 'seed_hideable' ? 'Seed' : v.inheritancePolicy === 'locked_mandatory' ? 'Locked' : 'Custom'}
      </div>
      <div className="shrink-0 w-10">
        <span className={`text-xs ${v.isActive ? 'text-green-600' : 'text-gray-400'}`}>{v.isActive ? 'Yes' : 'No'}</span>
      </div>
      {!multiSelectMode && (
        <div className="flex items-center gap-0.5 shrink-0">
          {v.canEdit && (
            <button className="p-1 text-gray-400 hover:text-orange-600 rounded" title="Edit" onClick={onEdit}>
              <Pencil size={14} />
            </button>
          )}
          {v.canHide && (
            <button className="p-1 text-gray-400 hover:text-orange-600 rounded" title="Hide/Show" onClick={onHide}>
              <EyeOff size={14} />
            </button>
          )}
          {v.canDelete && (
            <button className="p-1 text-gray-400 hover:text-red-500 rounded" title="Delete" onClick={onDelete}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Droppable Group Zone (wraps group header to be a drop target)
// ============================================================

function DroppableGroupZone({ groupId, children }: { groupId: string; children: React.ReactNode }) {
  const { isOver, setNodeRef } = useDroppable({ id: `drop-${groupId}`, data: { type: 'group', groupId } });
  return (
    <div ref={setNodeRef} className={`transition-colors ${isOver ? 'ring-2 ring-orange-400 ring-inset bg-orange-50/50' : ''}`}>
      {children}
    </div>
  );
}

function DroppableUngroupedZone({ children }: { children: React.ReactNode }) {
  const { isOver, setNodeRef } = useDroppable({ id: 'drop-ungrouped', data: { type: 'ungrouped' } });
  return (
    <div ref={setNodeRef} className={`transition-colors ${isOver ? 'ring-2 ring-blue-400 ring-inset bg-blue-50/30' : ''}`}>
      {children}
    </div>
  );
}

// ============================================================
// Slideout Header (mirrors InspectionsPage SlideoutHeader)
// ============================================================

function LookupSlideoutHeader({
  category,
  theme: t,
  onClose,
  onEdit,
}: {
  category: LookupCategory;
  theme: import('@ui-admin/contexts/ThemeContext').GridTheme;
  onClose: () => void;
  onEdit: () => void;
}) {
  const headerRef = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setCollapsed(entry.contentRect.width < 340);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!showOverflow) return;
    const handler = () => setShowOverflow(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showOverflow]);

  return (
    <div ref={headerRef} className="shrink-0 px-4 py-2.5 border-b flex items-center justify-between gap-2" style={{ borderColor: t.cardBorder }}>
      <h3 className="text-sm font-semibold truncate flex items-center gap-1.5 min-w-0" style={{ color: t.textPrimary }}>
        <span className="truncate">{category.displayName}</span>
        <a
          href={`/configuration/lookups/${category.lookupType}`}
          title={`Take me to ${category.displayName}`}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 text-gray-400 hover:text-orange-600 transition-colors">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </h3>

      {!collapsed ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={onEdit}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-orange-300 text-orange-700 hover:bg-orange-50 transition-colors whitespace-nowrap">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
          <button onClick={onClose}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors whitespace-nowrap">
            <X className="h-3.5 w-3.5" /> Close
          </button>
        </div>
      ) : (
        <div className="relative shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); setShowOverflow(prev => !prev); }}
            title="More actions"
            className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 transition-colors">
            <MoreVertical className="h-4 w-4" />
          </button>
          {showOverflow && (
            <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1"
              onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => { setShowOverflow(false); onEdit(); }}
                className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-gray-700 hover:bg-orange-50 hover:text-orange-700 transition-colors">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                onClick={() => { setShowOverflow(false); onClose(); }}
                className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">
                <X className="h-3.5 w-3.5" /> Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Main Component
// Renders DataGrid DIRECTLY (same pattern as InspectionsPage)
// ============================================================

export default function LookupsPage() {
  // --- Shared grid infrastructure from hook ---
  const grid = useGridPage({ gridId: GRID_ID, defaultPanelWidth: 600, seedTemplates: SEED_TEMPLATES });
  const { ready: dbReady, collectionStatuses } = useSystemDb();

  // --- Scope from Testing Tools (persisted in FeatureTierContext) ---
  const { user } = useAuth();
  const { lookupAdminScope: scopeContext, lookupValueScopeFilter: valueScopeFilter, enabledModules } = useFeatureTier();
  const enabledModulesKey = useMemo(() => [...enabledModules].sort().join('|'), [enabledModules]);
  const enabledModulesSet = useMemo(() => new Set(enabledModules), [enabledModulesKey]);
  const [moduleCatalog, setModuleCatalog] = useState<Array<{ key: string; label: string }>>([]);
  const categoryModuleOptions = useMemo(() => {
    return moduleCatalog.slice().sort((a, b) => a.label.localeCompare(b.label));
  }, [moduleCatalog]);
  const moduleLabelMap = useMemo<Record<string, string>>(() => {
    return Object.fromEntries(categoryModuleOptions.map((option) => [option.key, option.label]));
  }, [categoryModuleOptions]);
  const getModuleLabel = useCallback((key: string) => moduleLabelMap[key] || key, [moduleLabelMap]);

  useEffect(() => {
    let cancelled = false;
    listModuleOptions()
      .then((options) => {
        if (!cancelled) setModuleCatalog(options);
      })
      .catch(() => {
        if (!cancelled) setModuleCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // --- Core state ---
  const [categories, setCategories] = useState<LookupCategory[]>([]);
  const [dataVersion, setDataVersion] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<LookupCategory | null>(null);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [draftCategory, setDraftCategory] = useState<LookupCategory | null>(null);
  const [lookupPanelTab, setLookupPanelTab] = useState<'values' | 'audit'>('values');
  const [values, setValues] = useState<LookupValue[]>([]);
  const [editing, setEditing] = useState<LookupValue | null>(null);
  const [formCode, setFormCode] = useState('');
  const [formName, setFormName] = useState('');
  const [showAddRow, setShowAddRow] = useState(false);
  const [showValueModal, setShowValueModal] = useState(false);
  const [valueToEdit, setValueToEdit] = useState<LookupValue | null>(null);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncAllResult, setSyncAllResult] = useState<{ tenantCount: number; systemLookups: number; systemCategories: number } | null>(null);
  const [syncAllError, setSyncAllError] = useState<string | null>(null);

  // --- Modal State ---
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<LookupCategory | null>(null); // null = create
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<LookupCategory | null>(null);

  // --- Group state ---
  const [groups, setGroups] = useState<LookupGroup[]>([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState<LookupGroup | null>(null);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<LookupGroup | null>(null);
  const [groupDeleteHandleValues, setGroupDeleteHandleValues] = useState<'ungroup' | 'soft-delete'>('ungroup');
  const [valueToDelete, setValueToDelete] = useState<LookupValueRow | null>(null);
  const [bulkValueDeleteTargets, setBulkValueDeleteTargets] = useState<LookupValueRow[] | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // --- Multi-select & Drag-and-drop state ---
  const [panelTab, setPanelTab] = useState<'category' | 'values' | 'audit'>('category');
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedValueIds, setSelectedValueIds] = useState<Set<string>>(new Set());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [showMoveToGroupMenu, setShowMoveToGroupMenu] = useState(false);

  const exitMultiSelect = useCallback(() => {
    setMultiSelectMode(false);
    setSelectedValueIds(new Set());
    setShowMoveToGroupMenu(false);
  }, []);

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const filterVisibleCategories = useCallback((source: LookupCategory[]) => {
    return source.filter((c: LookupCategory) => {
      if (!c.availableScopes.includes(scopeContext)) return false;
      const policy = c.scopePolicy ?? DEFAULT_LOOKUP_SCOPE_POLICY;
      if (policy.scopeMode === 'all') return true;

      const categoryModules = getCategoryModules(c.module);
      const hasModule = categoryModules.length === 0 || categoryModules.some((module) => enabledModulesSet.has(module));

      if (policy.scopeMode === 'module') {
        return policy.enforceModule ? hasModule : true;
      }

      if (policy.scopeMode === 'entity') {
        return true;
      }

      if (policy.scopeMode === 'entityAndModule') {
        return !policy.enforceModule || hasModule;
      }

      return hasModule;
    });
  }, [scopeContext, enabledModulesSet]);

  // --- Async data fetching ---
  const loadCategories = useCallback(() => {
    setIsLoadingCategories(true);
    listLookupCategories({ limit: 1000 }).then((res) => {
      setCategories(filterVisibleCategories(res.data));
    }).catch(() => {
      setCategories([]);
    }).finally(() => {
      setIsLoadingCategories(false);
    });
  }, [filterVisibleCategories, scopeContext]);

  const loadValues = useCallback((lt: string) => {
    listLookupValues(
      lt,
      scopeContext === 'system'
        ? undefined
        : { activeModules: Array.from(enabledModulesSet) },
    ).then((v) => setValues(v)).catch((err) => {
      console.warn('[LookupsPage] Failed to load lookup values:', err);
    });
  }, [enabledModulesSet, scopeContext]);

  const loadGroups = useCallback((lt: string) => {
    listLookupGroups(lt).then((g) => setGroups(g)).catch((err) => {
      console.warn('[LookupsPage] Failed to load lookup groups:', err);
      setGroups([]);
    });
  }, []);

  // --- Multi-select & Drag-and-drop handlers (after loadValues) ---
  const handleSelectValue = useCallback((id: string, _shiftKey: boolean) => {
    setSelectedValueIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleValueDoubleClick = useCallback((id: string) => {
    setMultiSelectMode(true);
    setSelectedValueIds(new Set([id]));
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || !selectedCategory) return;

    const overData = over.data.current as { type: string; groupId?: string; valueId?: string } | undefined;
    if (!overData) return;

    const draggedId = active.id as string;
    if (overData.type === 'value' && overData.valueId && overData.valueId !== draggedId) {
      reorderLookupValue(selectedCategory.lookupType, draggedId, overData.valueId)
        .then(() => loadValues(selectedCategory.lookupType))
        .catch((err) => {
          console.error('[LookupsPage] Failed to reorder lookup values:', err);
        });
      return;
    }

    // Only accept drops on group zones or the ungrouped zone
    let targetGroupId: string | null;
    if (overData.type === 'group' && overData.groupId) {
      targetGroupId = overData.groupId;
    } else if (overData.type === 'ungrouped') {
      targetGroupId = null;
    } else {
      return; // Dropped on something that isn't a valid drop target
    }

    const idsToMove = selectedValueIds.has(draggedId)
      ? Array.from(selectedValueIds)
      : [draggedId];

    bulkAssignValuesToGroup(idsToMove, targetGroupId).then(() => {
      loadValues(selectedCategory.lookupType);
      setSelectedValueIds(new Set());
    }).catch(() => {});
  }, [selectedCategory, selectedValueIds, loadValues]);

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
  }, []);

  // --- Handlers ---
  const openCategory = useCallback((cat: LookupCategory) => {
    setSelectedCategory(cat);
    setDraftCategory(cat);
    setIsEditingCategory(false);
    setEditing(null);
    setPanelTab('category');
    setFormCode('');
    setFormName('');
    setEditing(null);
    setFormCode('');
    setFormName('');
    setCollapsedGroups(new Set());
    exitMultiSelect();
    loadValues(cat.lookupType);
    loadGroups(cat.lookupType);
  }, [loadValues, loadGroups, exitMultiSelect]);

  const handleCreateCategory = () => {
    setCategoryToEdit(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (cat: LookupCategory) => {
    setCategoryToEdit(cat);
    setShowCategoryModal(true);
  };

  const handleDeleteCategoryRequest = (cat: LookupCategory) => {
    setCategoryToDelete(cat);
    setShowDeleteModal(true);
  };

  // Helper: fetch fresh categories, unmount grid briefly, remount with new data
  const refreshGrid = useCallback((afterFetch?: (data: LookupCategory[]) => void) => {
    setIsRefreshing(true);
    listLookupCategories({ limit: 1000 }).then((res) => {
      const filtered = filterVisibleCategories(res.data);
      setCategories(filtered);
      afterFetch?.(res.data);
      // Let React unmount the grid for one frame, then remount
      requestAnimationFrame(() => setIsRefreshing(false));
    });
  }, [filterVisibleCategories, scopeContext]);

  const onCategorySaved = (savedCategory?: LookupCategory) => {
    const wasEdit = !!categoryToEdit;
    refreshGrid((allData) => {
      if (savedCategory) {
        const fresh = allData.find((c) => c.lookupType === savedCategory.lookupType);
        if (wasEdit) {
          // Edit: highlight the row briefly, update selectedCategory only if panel is already open for it
          setHighlightedRowId(savedCategory.lookupType);
          if (highlightTimer.current) clearTimeout(highlightTimer.current);
          highlightTimer.current = setTimeout(() => setHighlightedRowId(null), 3000);
          setSelectedCategory((prev) => {
            if (!prev || prev.lookupType !== savedCategory.lookupType) return prev;
            return fresh || prev;
          });
        } else if (fresh) {
          // Add: open the slide-out panel for the new category
          openCategory(fresh);
        }
      }
    });
    setShowCategoryModal(false);
    setCategoryToEdit(null);
  };

  const handleRefreshData = useCallback(() => {
    refreshGrid();
  }, [refreshGrid]);

  const categoriesSyncMarker = collectionStatuses.system_lookup_categories?.lastSyncAt || collectionStatuses.system_lookup_categories?.status || '';
  const valuesSyncMarker = collectionStatuses.system_lookups?.lastSyncAt || collectionStatuses.system_lookups?.status || '';
  const groupsSyncMarker = collectionStatuses.system_lookup_groups?.lastSyncAt || collectionStatuses.system_lookup_groups?.status || '';

  useEffect(() => {
    if (!dbReady) return;
    loadCategories();
  }, [dbReady, categoriesSyncMarker, loadCategories]);

  useEffect(() => {
    if (!dbReady || !selectedCategory) return;
    loadValues(selectedCategory.lookupType);
  }, [dbReady, selectedCategory, valuesSyncMarker, loadValues]);

  useEffect(() => {
    if (!dbReady || !selectedCategory) return;
    loadGroups(selectedCategory.lookupType);
  }, [dbReady, selectedCategory, groupsSyncMarker, loadGroups]);

  const onCategoryDeleted = () => {
    const deletedType = categoryToDelete?.lookupType;
    refreshGrid();
    setShowDeleteModal(false);
    setCategoryToDelete(null);
    if (selectedCategory && deletedType && selectedCategory.lookupType === deletedType) {
      closePanel();
    }
  };

  // Extract stable setter (React state setters are stable across renders)
  const setPanelVisible = grid.setPanelVisible;

  const closePanel = useCallback(() => {
    setPanelVisible(false);
    setTimeout(() => {
      setSelectedCategory(null);
      setDraftCategory(null);
      setIsEditingCategory(false);
      setValues([]);
      setEditing(null);
      setFormCode('');
      setFormName('');
    }, 300);
  }, [setPanelVisible]);

  const startInlineCategoryEdit = useCallback(() => {
    if (!selectedCategory) return;
    setDraftCategory(selectedCategory);
    setIsEditingCategory(true);
  }, [selectedCategory]);

  const cancelInlineCategoryEdit = useCallback(() => {
    setDraftCategory(selectedCategory);
    setIsEditingCategory(false);
  }, [selectedCategory]);

  const saveInlineCategory = useCallback(async () => {
    if (!draftCategory) return;
    const saved = await saveLookupCategory(draftCategory);
    setSelectedCategory(saved);
    setDraftCategory(saved);
    setIsEditingCategory(false);
    refreshGrid((allData) => {
      const fresh = allData.find((c) => c.lookupType === saved.lookupType);
      if (fresh) setSelectedCategory(fresh);
    });
  }, [draftCategory, refreshGrid]);

  // Animate panel in when a category is selected (same as InspectionsPage)
  useEffect(() => {
    if (selectedCategory) {
      requestAnimationFrame(() => setPanelVisible(true));
    } else {
      setPanelVisible(false);
    }
  }, [selectedCategory, setPanelVisible]);

  const canCreate = selectedCategory?.manageableScopes?.includes(scopeContext) ?? false;

  // Group management: available at all tiers, gated only by canCreate (manageableScopes)
  const canManageGroups = true;

  const selectedCount = selectedValueIds.size;

  // Escape key exits multi-select mode
  useEffect(() => {
    if (!multiSelectMode) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') exitMultiSelect(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [multiSelectMode, exitMultiSelect]);

  const handleFormSave = useCallback(() => {
    if (!selectedCategory || !formCode.trim() || !formName.trim()) return;
    const base: LookupValue = editing
      ? { ...editing, code: formCode.trim(), name: formName.trim() }
      : {
          id: `lkv-${selectedCategory.lookupType}-${Date.now()}`,
          lookupType: selectedCategory.lookupType,
          code: formCode.trim().toLowerCase().replace(/\s+/g, '_'),
          name: formName.trim(),
          scope: scopeContext, originScope: scopeContext,
          inheritancePolicy: 'custom_owned',
          sortOrder: values.length + 1,
          isActive: true, _deleted: false,
        };
    saveLookupValue(base).then(() => {
      setEditing(null); setFormCode(''); setFormName(''); setShowAddRow(false);
      loadValues(selectedCategory.lookupType);
    }).catch((err) => {
      console.warn('[LookupsPage] Failed to save lookup value:', err);
    });
  }, [editing, formCode, formName, loadValues, scopeContext, selectedCategory, values.length]);

  // --- Derived value rows ---
  const valueRows = useMemo<LookupValueRow[]>(() => {
    let filtered = values;
    if (valueScopeFilter !== 'all') filtered = values.filter((v) => v.scope === valueScopeFilter);
    const canManageCategory = selectedCategory?.manageableScopes.includes(scopeContext) ?? false;

    return filtered.map((v) => {
      const basePermissions = buildPermissions(v, scopeContext);

      return {
        ...v,
        ...basePermissions,
        canManage: canManageCategory && canManageGroups,
      };
    });
  }, [values, scopeContext, valueScopeFilter, selectedCategory, canManageGroups]);

  // ============================================================
  // Category columns (DataGrid)
  // ============================================================

  const categoryColumns = useMemo<DataGridColumn<LookupCategory>[]>(() => [
    { id: 'displayName', header: 'Category', accessorFn: (r: LookupCategory) => r.displayName, width: 240, minWidth: 180,
      cell: ({ row }: { row: { original: LookupCategory } }) => (<div className="flex flex-col"><div className="font-medium text-sm">{row.original.displayName}</div><div className="text-xs text-gray-400">{row.original.lookupType}</div></div>) },
    { id: 'description', header: 'Description', accessorFn: (r: LookupCategory) => r.description, width: 280, minWidth: 200 },
    { id: 'module', header: 'Module', accessorFn: (r: LookupCategory) => getCategoryModules(r.module).length ? getCategoryModules(r.module).map((m) => getModuleLabel(m)).join(', ') : 'Core', width: 180, minWidth: 140,
      cell: ({ row }: { row: { original: LookupCategory } }) => {
        const modules = getCategoryModules(row.original.module);
        return modules.length ? <span className="text-sm">{modules.map((m) => getModuleLabel(m)).join(', ')}</span> : <span className="text-sm text-gray-400">Core</span>;
      } },
    { id: 'isSystemManaged', header: 'System Managed', accessorFn: (r: LookupCategory) => r.isSystemManaged, width: 120, minWidth: 100,
      cell: ({ row }: { row: { original: LookupCategory } }) => <span className="text-sm">{row.original.isSystemManaged ? 'Yes' : 'No'}</span> },
  ], [getModuleLabel]);

  const isSystemScope = scopeContext === 'system';

  const handleSyncAllDepts = useCallback(async () => {
    if (isSyncingAll) return;
    setIsSyncingAll(true);
    setSyncAllResult(null);
    setSyncAllError(null);
    try {
      const data = await syncLookupsToAllTenants();
      setSyncAllResult({
        tenantCount: data.tenantCount,
        systemLookups: data.systemLookups,
        systemCategories: data.systemCategories,
      });
    } catch (err: any) {
      console.error('[LookupsPage] sync all error:', err);
      setSyncAllError(err?.message ?? 'Sync failed');
    } finally {
      setIsSyncingAll(false);
    }
  }, [isSyncingAll]);

  const categoryRowActions = useMemo<DataGridRowAction<LookupCategory>[]>(() => [
    { id: 'open', label: 'Open Values', icon: Eye, onClick: (row: LookupCategory) => { openCategory(row); } },
    { id: 'edit', label: 'Edit Category', icon: Pencil, hidden: () => !isSystemScope, onClick: (row: LookupCategory) => { handleEditCategory(row); } },
    { id: 'delete', label: 'Delete Category', icon: Trash2, hidden: () => !isSystemScope, variant: 'danger' as any, onClick: (row: LookupCategory) => { handleDeleteCategoryRequest(row); } },
  ], [openCategory, isSystemScope]);

  // ============================================================
  // Stable callbacks for DataGrid (prevents re-render cascade)
  // ============================================================
  const getRowId = useCallback((row: LookupCategory) => row.lookupType, []);
  const handleRowClick = useCallback((row: LookupCategory) => {
    openCategory(row);
  }, [openCategory]);

  // ============================================================
  // Template count (for header badge) — memoized to avoid localStorage reads every render
  // ============================================================
  const templateCount = useMemo(() => loadTemplates(GRID_ID, SEED_TEMPLATES).length, []);

  const handleExportSelectedValues = useCallback(() => {
    const selected = valueRows.filter((v) => selectedValueIds.has(v.id));
    if (selected.length === 0) {
      exitMultiSelect();
      return;
    }

    const escapeCsv = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Name', 'Code', 'Scope', 'Inheritance Policy', 'Active', 'Group', 'Categories', 'Modules'],
      ...selected.map((value) => [
        value.name,
        value.code,
        value.scope,
        value.inheritancePolicy,
        value.isActive ? 'Yes' : 'No',
        value.group?.groupName ?? '',
        Array.isArray(value.category) ? value.category.join('; ') : (value.category ?? ''),
        value.inspectionModules?.join('; ') ?? '',
      ]),
    ];

    const csv = rows.map((row) => row.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${selectedCategory?.lookupType ?? 'lookup-values'}-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    exitMultiSelect();
  }, [exitMultiSelect, selectedCategory?.lookupType, selectedValueIds, valueRows]);

  // ============================================================
  // Render — DataGrid rendered DIRECTLY (same as InspectionsPage)
  // ============================================================

  return (
    <>
      <style>{grid.themeCssString}</style>
      <div className="theme-grid flex flex-col overflow-hidden h-full">
        {/* Page Header */}
        <div className="shrink-0 px-4 pt-3 pb-2 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Lookups</h1>
            <p className="mt-1">Manage lookup categories and their values</p>
          </div>
          <div className="flex items-center gap-2">
            {isSystemScope && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSyncAllDepts}
                  disabled={isSyncingAll}
                  title="Push all locked_mandatory lookups to every active department"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  <Send className={`h-4 w-4 ${isSyncingAll ? 'animate-pulse' : ''}`} />
                  {isSyncingAll ? 'Syncing...' : 'Sync to All Departments'}
                </button>
                {syncAllResult && (
                  <span className="text-xs text-green-600">
                    Synced to {syncAllResult.tenantCount} dept{syncAllResult.tenantCount !== 1 ? 's' : ''} · {syncAllResult.systemLookups} values · {syncAllResult.systemCategories} categories
                  </span>
                )}
                {syncAllError && (
                  <span className="text-xs text-red-500" title={syncAllError}>Sync failed</span>
                )}
              </div>
            )}
            <button onClick={() => grid.setShowTemplatesModal(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
              <LayoutGrid className="h-4 w-4" /> Templates
              {grid.appliedTemplateName && (
                <span className="ml-0.5 max-w-[140px] truncate text-xs font-semibold text-orange-700" title={grid.appliedTemplateName}>
                  {grid.appliedTemplateName}
                </span>
              )}
              {templateCount > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-bold">
                  {templateCount}
                </span>
              )}
            </button>
            <button onClick={handleRefreshData}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Grid + slide-out — EXACTLY matches InspectionsPage layout */}
        <div className="flex-1 min-h-0 flex overflow-hidden" ref={grid.containerRef}>
          {/* DataGrid rendered DIRECTLY — no wrapper component */}
          <div className="flex-1 min-w-0 px-2 pb-1.5 transition-all duration-300 ease-in-out" style={{ minWidth: 200 }}>
            {isRefreshing ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">Refreshing...</div>
            ) : (<DataGrid<LookupCategory>
              key={grid.gridStateKey}
              gridId={GRID_ID}
              columns={categoryColumns}
              data={categories}
              getRowId={getRowId}
              searchPlaceholder="Search lookup categories..."
              rowActions={categoryRowActions}
              toolbarActions={isSystemScope ? (
                <button
                  onClick={handleCreateCategory}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-white rounded-lg text-sm font-medium transition-colors"
                  style={{ background: grid.theme.accent }}>
                  <Plus className="h-4 w-4" /> Add Category
                </button>
              ) : undefined}
              defaultPageSize={25}
              pageSizeOptions={[25, 50, 100]}
              containerClassName="h-full"
              onRowClick={handleRowClick}
              activeRowId={highlightedRowId || selectedCategory?.lookupType || null}
              initialState={grid.gridState}
              onStateChange={grid.handleGridStateChange}
              isTier2={grid.isTier2}
            />)}
          </div>

          {/* Animated Slide-out panel with draggable resizer — EXACTLY matches InspectionsPage */}
          {selectedCategory && (
            <div
              className="shrink-0 flex flex-row overflow-hidden"
              style={{
                width: grid.panelVisible ? Math.min(grid.panelWidth, (grid.containerRef.current?.offsetWidth ?? 9999) - 215) : 0,
                opacity: grid.panelVisible ? 1 : 0,
                transition: grid.isResizing.current ? 'none' : 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-in-out',
                maxWidth: 'calc(100% - 200px)',
              }}
            >
              {/* Draggable resizer handle */}
              <div
                className="shrink-0 flex items-center justify-center cursor-col-resize group"
                style={{ width: 6 }}
                onMouseDown={grid.startResize}
              >
                <div
                  className="h-full transition-colors duration-150"
                  style={{ width: 3, borderRadius: 2, background: grid.theme.cardBorder }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = grid.theme.accent; e.currentTarget.style.width = '5px'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = grid.theme.cardBorder; e.currentTarget.style.width = '3px'; }}
                />
              </div>

              {/* Panel content */}
              <div className="flex-1 min-w-0 flex flex-col" style={{ background: grid.theme.cardBg }}>
                {/* Panel Header — matches InspectionsPage SlideoutHeader */}
                <LookupSlideoutHeader
                  category={selectedCategory}
                  theme={grid.theme}
                  onClose={closePanel}
                  onEdit={startInlineCategoryEdit}
                />

                <div className="flex shrink-0 border-b" style={{ borderColor: grid.theme.cardBorder }}>
                  {(['category', 'values', 'audit'] as const).map((t) => (
                    <button key={t} onClick={() => setPanelTab(t)}
                      className="px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors"
                      style={{ borderBottomColor: panelTab === t ? grid.theme.accent : 'transparent', color: panelTab === t ? grid.theme.accentText : grid.theme.textSecondary }}>
                      {t === 'category' ? 'Category' : t === 'values' ? 'Values' : 'Audit Log'}
                    </button>
                  ))}
                </div>

                {panelTab === 'category' && draftCategory && (
                  <div className="flex-1 min-h-0 overflow-y-auto border-b px-4 py-3" style={{ borderColor: grid.theme.cardBorder }}>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Display Name</label>
                        <input className="w-full border rounded-md px-3 py-2" value={draftCategory.displayName} onChange={(e) => setDraftCategory({ ...draftCategory, displayName: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Lookup Type</label>
                        <input className="w-full border rounded-md px-3 py-2 bg-gray-100" value={draftCategory.lookupType} disabled />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                        <textarea className="w-full border rounded-md px-3 py-2 h-20" value={draftCategory.description} onChange={(e) => setDraftCategory({ ...draftCategory, description: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Modules</label>
                        <ModuleMultiSelectDropdown
                          options={categoryModuleOptions}
                          selected={Array.isArray(draftCategory.module) ? draftCategory.module : draftCategory.module ? [draftCategory.module] : []}
                          onChange={(next) => setDraftCategory({ ...draftCategory, module: next })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Scope Mode</label>
                        <select
                          className="w-full border rounded-md px-3 py-2"
                          value={draftCategory.scopePolicy?.scopeMode || 'all'}
                          onChange={(e) => setDraftCategory({
                            ...draftCategory,
                            scopePolicy: deriveScopePolicy(e.target.value as 'all' | 'entity' | 'module' | 'entityAndModule'),
                          })}
                        >
                          <option value="all">All</option>
                          <option value="entity">Entity</option>
                          <option value="module">Module</option>
                          <option value="entityAndModule">Entity + Module</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Available Scopes</label>
                        <div className="grid grid-cols-3 gap-2 border rounded-md p-3 bg-gray-50">
                          {SCOPE_OPTIONS.map((scope) => (
                            <label key={scope} className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={draftCategory.availableScopes.includes(scope)}
                                onChange={() => setDraftCategory({
                                  ...draftCategory,
                                  availableScopes: draftCategory.availableScopes.includes(scope)
                                    ? draftCategory.availableScopes.filter((s) => s !== scope)
                                    : [...draftCategory.availableScopes, scope],
                                })}
                                className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                              />
                              <span>{labelScope(scope)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Manageable Scopes</label>
                        <div className="grid grid-cols-3 gap-2 border rounded-md p-3 bg-gray-50">
                          {SCOPE_OPTIONS.map((scope) => (
                            <label key={scope} className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={draftCategory.manageableScopes.includes(scope)}
                                onChange={() => setDraftCategory({
                                  ...draftCategory,
                                  manageableScopes: draftCategory.manageableScopes.includes(scope)
                                    ? draftCategory.manageableScopes.filter((s) => s !== scope)
                                    : [...draftCategory.manageableScopes, scope],
                                })}
                                className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                              />
                              <span>{labelScope(scope)}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-xs text-orange-900 mt-3">
                      <div className="font-medium mb-1">Policy summary</div>
                      <div>Modules: {Array.isArray(draftCategory.module) && draftCategory.module.length ? draftCategory.module.map((key) => getModuleLabel(key)).join(', ') : 'unrestricted'}</div>
                      <div>Available scopes: {draftCategory.availableScopes.length ? draftCategory.availableScopes.map(labelScope).join(', ') : 'none'}</div>
                      <div>Manageable scopes: {draftCategory.manageableScopes.length ? draftCategory.manageableScopes.map(labelScope).join(', ') : 'none'}</div>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-3">
                      <div className="text-xs text-gray-500">Edit category metadata here. Values live in the next tab.</div>
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={cancelInlineCategoryEdit} className="px-3 py-2 text-sm border rounded-md">Revert</button>
                        <button type="button" onClick={() => void saveInlineCategory()} className="px-3 py-2 text-sm bg-orange-600 text-white rounded-md">Save</button>
                      </div>
                    </div>
                  </div>
                )}

                {panelTab === 'audit' && (
                  <AuditLogTimeline
                    documentId={selectedCategory.lookupType}
                    collection="lookups"
                    theme={grid.theme}
                    active={panelTab === 'audit'}
                    fetchFn={fetchAuditLogs}
                  />
                )}

                {panelTab === 'values' && <>
                {/* Metadata row & Actions */}
                <div className="px-2.5 pt-2 pb-1 flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
                    <span className="px-2 py-0.5 rounded-full bg-gray-100">Type: {selectedCategory.lookupType}</span>
                    <span className="px-2 py-0.5 rounded-full bg-gray-100">
                      Modules: {Array.isArray(selectedCategory.module) ? selectedCategory.module.length : selectedCategory.module ? 1 : 0}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-gray-100">
                      Scopes: {selectedCategory.availableScopes.length}
                    </span>
                  </div>
                  <p className="text-xs truncate" style={{ color: grid.theme.textMuted }}>
                    {selectedCategory.description}
                  </p>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {canCreate && canManageGroups && (
                      <button type="button"
                        onClick={() => { setGroupToEdit(null); setShowGroupModal(true); }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border border-orange-400 text-orange-600 hover:bg-orange-50 transition-colors whitespace-nowrap">
                        <FolderPlus size={12} /> Add Group
                      </button>
                    )}
                    {canCreate && (
                      <button type="button"
                        onClick={() => { setValueToEdit(null); setShowValueModal(true); }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors whitespace-nowrap">
                        <Plus size={12} /> Add Lookup
                      </button>
                    )}
                  </div>
                </div>

                {/* ===== BULK ACTION BAR (matches DataGrid pattern) ===== */}
                {multiSelectMode && selectedCount > 0 && (
                  <div className="shrink-0 px-3 py-2 bg-orange-50 border-b border-orange-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" title="Select all values"
                        checked={selectedCount === valueRows.filter(v => v.canManage).length}
                        onChange={() => {
                          const manageableIds = valueRows.filter(v => v.canManage).map(v => v.id);
                          if (selectedCount === manageableIds.length) {
                            setSelectedValueIds(new Set());
                          } else {
                            setSelectedValueIds(new Set(manageableIds));
                          }
                        }}
                        className="w-3.5 h-3.5 text-orange-600 rounded focus:ring-orange-500 cursor-pointer" />
                      <span className="text-sm font-medium text-orange-800">{selectedCount} selected</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {/* Move to Group */}
                      <div className="relative">
                        <button
                          onClick={() => setShowMoveToGroupMenu(prev => !prev)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                          <FolderPlus className="h-3 w-3" /> Move to Group
                        </button>
                        {showMoveToGroupMenu && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1">
                            <button
                              onClick={() => {
                                bulkAssignValuesToGroup(Array.from(selectedValueIds), null).then(() => {
                                  if (selectedCategory) loadValues(selectedCategory.lookupType);
                                  exitMultiSelect();
                                });
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                              None (ungrouped)
                            </button>
                            {groups.map(g => (
                              <button key={g.id}
                                onClick={() => {
                                  bulkAssignValuesToGroup(Array.from(selectedValueIds), g.id).then(() => {
                                    if (selectedCategory) loadValues(selectedCategory.lookupType);
                                    exitMultiSelect();
                                  });
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 transition-colors flex items-center gap-2">
                                {g.icon && (() => { const IC = getIconComponent(g.icon); return IC ? <IC size={12} style={{ color: g.color || '#6B7280' }} /> : null; })()}
                                <span className="truncate">{g.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Toggle Active */}
                      <button
                        onClick={() => {
                          if (!selectedCategory) return;
                          const ids = Array.from(selectedValueIds);
                          const selected = valueRows.filter(v => ids.includes(v.id));
                          const allActive = selected.every(v => v.isActive);
                          Promise.all(selected.map(v => saveLookupValue({ ...v, isActive: !allActive }))).then(() => {
                            loadValues(selectedCategory.lookupType);
                            exitMultiSelect();
                          });
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                        <ToggleLeft className="h-3 w-3" /> Toggle Active
                      </button>
                      {/* Delete */}
                      {valueRows.filter(v => selectedValueIds.has(v.id)).every(v => v.canDelete) && (
                        <button
                          onClick={() => {
                            if (!selectedCategory) return;
                            const selected = valueRows.filter(v => selectedValueIds.has(v.id));
                            setBulkValueDeleteTargets(selected);
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 transition-colors">
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      )}
                      {/* Export */}
                      <button
                        onClick={handleExportSelectedValues}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                        <Download className="h-3 w-3" /> Export
                      </button>
                      {/* Close multi-select */}
                      <button onClick={exitMultiSelect} title="Exit selection mode"
                        className="ml-1 p-1 text-gray-400 hover:text-gray-600 rounded">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Grouped Values Display with Drag-and-Drop */}
                <DndContext sensors={multiSelectMode ? undefined : dndSensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
                <div className="flex-1 overflow-auto">
                  {valueRows.length === 0 && groups.length === 0 && (
                    <div className="px-3 py-6 text-center text-gray-400 text-sm">No values for this category</div>
                  )}

                  {/* Ungrouped values — displayed at top (droppable to ungroup) */}
                  <DroppableUngroupedZone>
                    <SortableContext items={valueRows.filter((v) => !v.group).map((v) => v.id)} strategy={verticalListSortingStrategy}>
                    {valueRows.filter((v) => !v.group).map((v) => (
                      <ValueRow key={v.id} v={v}
                        selected={selectedValueIds.has(v.id)} multiSelectMode={multiSelectMode}
                        onSelect={handleSelectValue} onDoubleClick={handleValueDoubleClick}
                        onEdit={() => { setValueToEdit(v); setShowValueModal(true); }}
                        onHide={() => { setLookupHidden(v.id, !v.isHiddenAtLowerLevels).then(() => { if (selectedCategory) loadValues(selectedCategory.lookupType); }); }}
                        onDelete={() => { setValueToDelete(v); }} />
                    ))}
                    </SortableContext>
                    {/* Minimal drop hint when no ungrouped values exist */}
                    {valueRows.filter((v) => !v.group).length === 0 && groups.length > 0 && (
                      <div className="px-3 py-2 text-[10px] text-gray-400 italic border-b border-dashed">Drop here to ungroup</div>
                    )}
                  </DroppableUngroupedZone>

                  {/* Group sections */}
                  {groups.map((g) => {
                    const memberRows = valueRows.filter((v) => v.group?.groupId === g.id);
                    const isCollapsed = collapsedGroups.has(g.id);
                    const toggleCollapse = () => setCollapsedGroups((prev) => {
                      const next = new Set(prev);
                      if (next.has(g.id)) next.delete(g.id); else next.add(g.id);
                      return next;
                    });
                    return (
                      <DroppableGroupZone key={g.id} groupId={g.id}>
                        {/* Group header */}
                        <div
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-y border-gray-200 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                          onClick={toggleCollapse}
                        >
                          {isCollapsed ? <ChevronRight size={14} className="text-gray-400 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 shrink-0" />}
                          {g.icon && (() => { const IC = getIconComponent(g.icon); return IC ? <IC size={14} style={{ color: g.color || '#6B7280' }} /> : null; })()}
                          <span className="text-sm font-semibold text-gray-700 truncate" style={g.color ? { color: g.color } : undefined}>{g.name}</span>
                          <span className="text-[10px] text-gray-400 bg-gray-200 rounded-full px-1.5 py-0.5 font-medium">{memberRows.length}</span>
                          <div className="ml-auto flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                            {canCreate && canManageGroups && (
                              <button className="p-1 text-gray-400 hover:text-orange-600 rounded" title="Edit Group"
                                onClick={() => { setGroupToEdit(g); setShowGroupModal(true); }}>
                                <Pencil size={13} />
                              </button>
                            )}
                            {canCreate && canManageGroups && (
                              <button className="p-1 text-gray-400 hover:text-red-500 rounded" title="Delete Group"
                                onClick={() => { setGroupToDelete(g); setShowDeleteGroupModal(true); }}>
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Group members */}
                        {!isCollapsed && (
                          <SortableContext items={memberRows.map((v) => v.id)} strategy={verticalListSortingStrategy}>
                          {memberRows.map((v) => (
                          <ValueRow key={v.id} v={v} indented
                            selected={selectedValueIds.has(v.id)} multiSelectMode={multiSelectMode}
                            onSelect={handleSelectValue} onDoubleClick={handleValueDoubleClick}
                            onEdit={() => { setValueToEdit(v); setShowValueModal(true); }}
                            onHide={() => { setLookupHidden(v.id, !v.isHiddenAtLowerLevels).then(() => { if (selectedCategory) loadValues(selectedCategory.lookupType); }); }}
                            onDelete={() => { setValueToDelete(v); }} />
                        ))}
                          </SortableContext>
                        )}
                        {!isCollapsed && memberRows.length === 0 && (
                          <div className="px-3 py-3 pl-8 text-xs text-gray-400 italic border-b">No values in this group</div>
                        )}
                      </DroppableGroupZone>
                    );
                  })}
                </div>

                {/* Drag overlay — ghost preview */}
                <DragOverlay>
                  {activeDragId ? (() => {
                    const draggedValue = valueRows.find((v) => v.id === activeDragId);
                    const count = selectedValueIds.has(activeDragId) ? selectedValueIds.size : 1;
                    return draggedValue ? (
                      <div className="bg-white border border-orange-300 rounded-md shadow-lg px-3 py-2 text-sm max-w-xs">
                        <div className="font-medium truncate">{draggedValue.name}</div>
                        {count > 1 && (
                          <div className="text-[10px] text-orange-600 font-medium mt-0.5">+ {count - 1} more selected</div>
                        )}
                      </div>
                    ) : null;
                  })() : null}
                </DragOverlay>
                </DndContext>
                </>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grid Templates Modal */}
      {grid.showTemplatesModal && (
        <GridTemplatesModal
          gridId={GRID_ID}
          gridType="lookups"
          seedTemplates={SEED_TEMPLATES}
          currentState={grid.currentGridState.current}
          onApply={grid.handleApplyTemplate}
          onClose={() => grid.setShowTemplatesModal(false)}
          currentUser={{ userId: user?.id ?? '', displayName: user?.name ?? '' }}
          isAdmin={grid.isAdmin}
          tier={grid.tier}
          knownColumnIds={categoryColumns.map((c) => c.id).filter((id) => id !== '_actions' && id !== '_select')}
        />
      )}

      {/* Add/Edit Category Modal */}
      {showCategoryModal && (
        <LookupCategoryModal
          category={categoryToEdit}
          moduleOptions={categoryModuleOptions}
          onClose={() => setShowCategoryModal(false)}
          onSave={onCategorySaved}
        />
      )}

      {/* Add/Edit Lookup Value Modal (shared ui-admin component) */}
      {showValueModal && selectedCategory && (
        <SharedLookupValueModal
          value={valueToEdit as any}
          lookupType={selectedCategory.lookupType}
          scopeContext={scopeContext}
          existingCount={values.length}
          availableGroups={groups as any}
          availableInspectionModules={categoryModuleOptions}
          canAssignGroup={canManageGroups}
          showSystemDefaultToggle
          onClose={() => { setShowValueModal(false); setValueToEdit(null); }}
          onSave={(saved) => {
            saveLookupValue(saved as any).then(() => {
              loadValues(selectedCategory.lookupType);
              setShowValueModal(false);
              setValueToEdit(null);
            }).catch((err) => {
              console.warn('[LookupsPage] Failed to save lookup value from modal:', err);
            });
          }}
        />
      )}

      {/* Delete Category Modal */}
      {showDeleteModal && categoryToDelete && (
        <ConfirmDeleteModal
          title="Delete Category?"
          confirmValue={categoryToDelete.lookupType}
          confirmLabel="category key"
          description={<>Are you sure you want to delete the category <strong>{categoryToDelete.displayName}</strong>? This will soft-delete the category and hide it from all lists.</>}
          deleteButtonLabel="Delete Category"
          onConfirm={async () => {
            await softDeleteLookupCategory(categoryToDelete.lookupType);
            onCategoryDeleted();
          }}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      {/* Add/Edit Group Modal */}
      {showGroupModal && selectedCategory && (
        <LookupGroupModal
          group={groupToEdit}
          lookupType={selectedCategory.lookupType}
          scopeContext={scopeContext}
          existingGroups={groups}
          onClose={() => { setShowGroupModal(false); setGroupToEdit(null); }}
          onSave={(saved) => {
            saveLookupGroup(saved).then(() => {
              loadGroups(selectedCategory.lookupType);
              loadValues(selectedCategory.lookupType);
              setShowGroupModal(false);
              setGroupToEdit(null);
            }).catch((err) => {
              console.warn('[LookupsPage] Failed to save lookup group from modal:', err);
            });
          }}
        />
      )}

      {/* Delete Value Modal — single value */}
      {valueToDelete && selectedCategory && (
        <ConfirmDeleteModal
          title="Delete Lookup Value?"
          confirmValue={valueToDelete.name}
          confirmLabel="value name"
          description={<>Are you sure you want to delete the lookup value <strong>{valueToDelete.name}</strong> ({valueToDelete.code})? This action cannot be undone.</>}
          deleteButtonLabel="Delete Value"
          onConfirm={async () => {
            await softDeleteLookupValue(valueToDelete.id);
            loadValues(selectedCategory.lookupType);
            setValueToDelete(null);
          }}
          onClose={() => setValueToDelete(null)}
        />
      )}

      {/* Bulk Delete Values Modal — multi-select */}
      {bulkValueDeleteTargets && bulkValueDeleteTargets.length > 0 && selectedCategory && (
        <ConfirmBulkDeleteModal
          title={`Delete ${bulkValueDeleteTargets.length} Lookup Value${bulkValueDeleteTargets.length === 1 ? '' : 's'}?`}
          description={<>This will permanently remove the following lookup value{bulkValueDeleteTargets.length === 1 ? '' : 's'}. This action cannot be undone.</>}
          records={bulkValueDeleteTargets.map(v => ({
            id: v.id,
            label: v.name,
            sublabel: v.code,
          } as BulkDeleteRecord))}
          onConfirm={async () => {
            await Promise.all(bulkValueDeleteTargets.map(v => softDeleteLookupValue(v.id)));
            loadValues(selectedCategory.lookupType);
            exitMultiSelect();
            setBulkValueDeleteTargets(null);
          }}
          onClose={() => setBulkValueDeleteTargets(null)}
        />
      )}

      {/* Delete Group Modal */}
      {showDeleteGroupModal && groupToDelete && selectedCategory && (() => {
        const memberCount = values.filter((v) => v.group?.groupId === groupToDelete.id).length;
        const isSystemOrOrg = groupToDelete.scope === 'system' || groupToDelete.scope === 'organization';
        return (
          <ConfirmDeleteModal
            title="Delete Group?"
            confirmValue={groupToDelete.name}
            confirmLabel="group name"
            description={<>Are you sure you want to permanently delete the group <strong>{groupToDelete.name}</strong>? This action cannot be undone.</>}
            warning={isSystemOrOrg ? <>This group is scoped to <strong>{groupToDelete.scope}</strong> level and is visible to all lower levels. Deleting it will affect users at those levels.</> : undefined}
            extraContent={memberCount > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-700">
                  This group contains <span className="text-orange-600">{memberCount}</span> lookup value{memberCount !== 1 ? 's' : ''}. How should they be handled?
                </p>
                <label className="flex items-start gap-2 p-2 rounded-md border cursor-pointer hover:bg-gray-50 transition-colors">
                  <input type="radio" name="handleValues" value="ungroup" checked={groupDeleteHandleValues === 'ungroup'}
                    onChange={() => setGroupDeleteHandleValues('ungroup')}
                    className="mt-0.5 w-3.5 h-3.5 text-orange-600 focus:ring-orange-500" />
                  <div>
                    <span className="text-sm font-medium text-gray-800">Ungroup values first</span>
                    <p className="text-xs text-gray-500">Remove values from this group, then delete the group. Values remain active.</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 p-2 rounded-md border cursor-pointer hover:bg-gray-50 transition-colors">
                  <input type="radio" name="handleValues" value="soft-delete" checked={groupDeleteHandleValues === 'soft-delete'}
                    onChange={() => setGroupDeleteHandleValues('soft-delete')}
                    className="mt-0.5 w-3.5 h-3.5 text-red-600 focus:ring-red-500" />
                  <div>
                    <span className="text-sm font-medium text-red-700">Delete values with group</span>
                    <p className="text-xs text-gray-500">Soft-delete all {memberCount} value{memberCount !== 1 ? 's' : ''} and permanently delete the group.</p>
                  </div>
                </label>
              </div>
            ) : undefined}
            deleteButtonLabel="Delete Group"
            onConfirm={async () => {
              await deleteLookupGroup(groupToDelete.id, groupDeleteHandleValues);
              loadGroups(selectedCategory.lookupType);
              loadValues(selectedCategory.lookupType);
              setShowDeleteGroupModal(false);
              setGroupToDelete(null);
              setGroupDeleteHandleValues('ungroup');
            }}
            onClose={() => { setShowDeleteGroupModal(false); setGroupToDelete(null); setGroupDeleteHandleValues('ungroup'); }}
          />
        );
      })()}
    </>
  );
}
// Lookup Category Modal (Add/Edit)
// ============================================================

function LookupCategoryModal({ category, moduleOptions, onClose, onSave }: {
  category: LookupCategory | null;
  moduleOptions: { key: string; label: string }[];
  onClose: () => void;
  onSave: (savedCategory?: LookupCategory) => void;
}) {
  const titleId = 'lookup-category-modal-title';
  const [lookupType, setLookupType] = useState(category?.lookupType || '');
  const [displayName, setDisplayName] = useState(category?.displayName || '');
  const [description, setDescription] = useState(category?.description || '');
  const [modules, setModules] = useState<string[]>(Array.isArray(category?.module) ? category.module : category?.module ? [category.module] : []);
  const [availableScopes, setAvailableScopes] = useState<LookupScope[]>(category?.availableScopes || ['system', 'organization', 'tenant']);
  const [manageableScopes, setManageableScopes] = useState<LookupScope[]>(category?.manageableScopes || ['system']);
  const [scopeMode, setScopeMode] = useState<'all' | 'entity' | 'module' | 'entityAndModule'>(category?.scopePolicy?.scopeMode || 'all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const moduleLabelMap = useMemo<Record<string, string>>(() => {
    return Object.fromEntries(moduleOptions.map((option) => [option.key, option.label]));
  }, [moduleOptions]);
  const getModuleLabel = useCallback((key: string) => moduleLabelMap[key] || key, [moduleLabelMap]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookupType || !displayName) return;
    setIsSubmitting(true);
    try {
      const saved = await saveLookupCategory({
        lookupType,
        displayName,
        description,
        module: modules,
        availableScopes,
        manageableScopes,
        scopePolicy: deriveScopePolicy(scopeMode),
        isSystemManaged: category?.isSystemManaged ?? false,
        updatedAt: category?.updatedAt,
      });
      onSave(saved);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col overflow-hidden max-h-[90vh]">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 id={titleId} className="font-semibold text-gray-900">{category ? 'Edit Category' : 'Add Category'}</h3>
          <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              type="text"
              required
              title="Display Name"
              placeholder="e.g. Contact Type"
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lookup Type (Key)</label>
            <input
              type="text"
              required
              disabled={!!category}
              title="Lookup Type Key"
              placeholder="e.g. contactType"
              className="w-full border rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
              value={lookupType}
              onChange={(e) => setLookupType(e.target.value)}
            />
            {!category && <p className="text-xs text-gray-500 mt-1">Unique key, e.g. "contactType"</p>}
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Modules (Optional)</label>
            </div>
            <div className="mt-1">
              <ModuleMultiSelectDropdown options={moduleOptions} selected={modules} onChange={setModules} />
            </div>
            <p className="text-xs text-gray-500 mt-1">Leave all unchecked to make the category unrestricted across modules.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Available Scopes</label>
              <div className="border rounded-md p-2 space-y-1 bg-gray-50">
                {SCOPE_OPTIONS.map((scope) => (
                  <label key={scope} className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={availableScopes.includes(scope)} onChange={() => setAvailableScopes((prev) => prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope])} className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500" />
                    <span>{labelScope(scope)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Manageable Scopes</label>
              <div className="border rounded-md p-2 space-y-1 bg-gray-50">
                {SCOPE_OPTIONS.map((scope) => (
                  <label key={scope} className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={manageableScopes.includes(scope)} onChange={() => setManageableScopes((prev) => prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope])} className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500" />
                    <span>{labelScope(scope)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scope Mode</label>
              <select value={scopeMode} onChange={(e) => setScopeMode(e.target.value as 'all' | 'entity' | 'module' | 'entityAndModule')} className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <option value="all">All</option>
                <option value="entity">Entity</option>
                <option value="module">Module</option>
                <option value="entityAndModule">Entity + Module</option>
              </select>
            </div>
          </div>
          <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-xs text-orange-900">
            <div className="font-medium mb-1">Policy summary</div>
            <div>Modules: {modules.length ? modules.map((key) => getModuleLabel(key)).join(', ') : 'unrestricted'}</div>
            <div>Scopes: {availableScopes.length ? availableScopes.map(labelScope).join(', ') : 'none'}</div>
            <div>Enforcement: {scopeMode === 'all' ? 'Unrestricted' : scopeMode === 'entity' ? 'Entity category only' : scopeMode === 'module' ? 'Module only' : 'Entity category and module'}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              title="Description"
              placeholder="Optional description"
              className="w-full border rounded-md px-3 py-2 text-sm h-20 resize-none"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="px-3 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50">
              {isSubmitting ? 'Saving...' : 'Save Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// (DeleteCategoryModal removed — now uses shared ConfirmDeleteModal)

// ============================================================
// Lookup Group Modal (Add/Edit)
// ============================================================

function LookupGroupModal({ group, lookupType, scopeContext, existingGroups, onClose, onSave }: {
  group: LookupGroup | null;
  lookupType: string;
  scopeContext: LookupScope;
  existingGroups: LookupGroup[];
  onClose: () => void;
  onSave: (g: LookupGroup) => void;
}) {
  const isEdit = !!group;
  const isSystemAdmin = scopeContext === 'system';

  const [name, setName] = useState(group?.name || '');
  const [code, setCode] = useState(group?.code || '');
  const [description, setDescription] = useState(group?.description || '');
  const [scope, setScope] = useState<LookupScope>(group?.scope || scopeContext);
  const [icon, setIcon] = useState(group?.icon || '');
  const [color, setColor] = useState(group?.color || '');
  const [sortOrder, setSortOrder] = useState(group?.sortOrder ?? existingGroups.length + 1);
  const [isActive, setIsActive] = useState(group?.isActive ?? true);
  const [isSystemDefault, setIsSystemDefault] = useState(group?.isSystemDefault ?? false);

  // Track whether the user has manually edited the code field
  const codeManuallyEdited = useRef(false);

  // Auto-generate code from name (only when not manually edited and not in edit mode)
  const nameToCode = (n: string) => n.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const handleNameChange = (val: string) => {
    setName(val);
    if (!isEdit && !codeManuallyEdited.current) {
      setCode(nameToCode(val));
    }
  };

  const handleCodeChange = (val: string) => {
    codeManuallyEdited.current = true;
    setCode(val);
  };

  // Real-time duplicate check scoped to this lookup category
  const normalizedCode = code.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const isDuplicateCode = useMemo(() => {
    if (!normalizedCode) return false;
    return existingGroups.some(g => g.code === normalizedCode && g.id !== group?.id);
  }, [normalizedCode, existingGroups, group?.id]);

  // Edit policy
  const [showEditPolicy, setShowEditPolicy] = useState(!!group?.editPolicy);
  const [orgCanEdit, setOrgCanEdit] = useState(group?.editPolicy?.orgCanEdit ?? true);
  const [orgCanHide, setOrgCanHide] = useState(group?.editPolicy?.orgCanHide ?? true);
  const [tenantCanEdit, setTenantCanEdit] = useState(group?.editPolicy?.tenantCanEdit ?? true);
  const [tenantCanHide, setTenantCanHide] = useState(group?.editPolicy?.tenantCanHide ?? true);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim() || isDuplicateCode) return;
    setIsSubmitting(true);

    const ep: LookupEditPolicy | undefined = (showEditPolicy && isSystemAdmin) ? {
      orgCanEdit: orgCanEdit || undefined,
      orgCanHide: orgCanHide || undefined,
      tenantCanEdit: tenantCanEdit || undefined,
      tenantCanHide: tenantCanHide || undefined,
    } : group?.editPolicy;

    const result: LookupGroup = {
      id: group?.id || `grp-${lookupType}-${Date.now()}`,
      lookupType,
      code: isEdit ? code.trim() : code.trim().toLowerCase().replace(/\s+/g, '_'),
      name: name.trim(),
      description: description.trim() || undefined,
      scope,
      parentGroupId: group?.parentGroupId,
      icon: icon.trim() || undefined,
      color: color.trim() || undefined,
      sortOrder,
      isActive,
      isSystemDefault,
      editPolicy: ep,
      createdAt: group?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onSave(result);
  };

  const inputCls = 'w-full border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-0.5';
  const sectionBtnCls = 'flex items-center justify-between w-full text-left text-xs font-semibold text-gray-700 hover:text-orange-600 py-1';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-xl flex flex-col overflow-hidden max-h-[90vh]">
        <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
          <h3 className="font-semibold text-gray-900">{isEdit ? 'Edit Group' : 'Add Group'}</h3>
          <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3 overflow-y-auto text-sm">
          {/* Name & Code */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Group Name</label>
              <input type="text" required title="Group Name" className={inputCls}
                value={name} onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g. Administrative Contacts" />
            </div>
            <div>
              <label className={labelCls}>Code</label>
              <input type="text" required disabled={isEdit} title="Code"
                className={`${inputCls} ${isEdit ? 'bg-gray-100 text-gray-500' : ''} ${isDuplicateCode ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : ''}`}
                value={code} onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="e.g. administrative" />
              {!isEdit && isDuplicateCode && (
                <p className="text-[10px] text-red-600 mt-0.5 font-medium">Code &apos;{normalizedCode}&apos; already exists in this category</p>
              )}
              {!isEdit && !isDuplicateCode && (
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {codeManuallyEdited.current ? 'Lowercase, underscores only' : 'Auto-generated from name — click to edit'}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className={labelCls}>Description</label>
            <input type="text" title="Description" className={inputCls}
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this group" />
          </div>

          {/* Sort, Active, System Default */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Sort Order</label>
              <input type="number" min={0} title="Sort Order" placeholder="0" className={inputCls}
                value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />
            </div>
            <div className="flex flex-col justify-end gap-1 pb-0.5">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
                  className="w-3.5 h-3.5 text-orange-600 rounded focus:ring-orange-500" />
                <span className="text-xs text-gray-700">Active</span>
              </label>
              {isSystemAdmin && (
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={isSystemDefault} onChange={(e) => setIsSystemDefault(e.target.checked)}
                    className="w-3.5 h-3.5 text-orange-600 rounded focus:ring-orange-500" />
                  <span className="text-xs text-gray-700">System Default</span>
                </label>
              )}
            </div>
          </div>

          {/* Icon & Color */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>Icon</label>
              <IconPicker value={icon || undefined} onChange={(v) => setIcon(v || '')} />
            </div>
            <div>
              <label className={labelCls}>Color</label>
              <div className="flex gap-1">
                <input type="color" title="Pick color" className="w-8 h-8 rounded border cursor-pointer"
                  value={color || '#6B7280'} onChange={(e) => setColor(e.target.value)} />
                <input type="text" title="Color hex value" className={`${inputCls} flex-1`}
                  value={color} onChange={(e) => setColor(e.target.value)} placeholder="#6B7280" />
              </div>
            </div>
          </div>

          {/* Preview */}
          {(name || icon || color) && (
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md border">
              <span className="text-[10px] text-gray-500">Preview:</span>
              {icon && (() => { const IC = getIconComponent(icon); return IC ? <IC size={16} style={{ color: color || '#6B7280' }} /> : null; })()}
              <span className="text-sm font-semibold" style={color ? { color } : undefined}>{name || 'Group Name'}</span>
            </div>
          )}

          {/* Edit Policy (System Admin only) */}
          {isSystemAdmin && (
            <div className="border rounded-md">
              <button type="button" className={`${sectionBtnCls} px-3`} onClick={() => setShowEditPolicy(!showEditPolicy)}>
                <span>Edit Policy — Lower-Level Permissions</span>
                <span className="text-gray-400">{showEditPolicy ? '▾' : '▸'}</span>
              </button>
              {showEditPolicy && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider col-span-2 mt-1">Organization Level</p>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={orgCanEdit} onChange={(e) => setOrgCanEdit(e.target.checked)}
                        className="w-3.5 h-3.5 text-orange-600 rounded focus:ring-orange-500" />
                      <span className="text-xs text-gray-700">Org can edit this group</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={orgCanHide} onChange={(e) => setOrgCanHide(e.target.checked)}
                        className="w-3.5 h-3.5 text-orange-600 rounded focus:ring-orange-500" />
                      <span className="text-xs text-gray-700">Org can hide this group</span>
                    </label>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider col-span-2 mt-2">Tenant / Department Level</p>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={tenantCanEdit} onChange={(e) => setTenantCanEdit(e.target.checked)}
                        className="w-3.5 h-3.5 text-orange-600 rounded focus:ring-orange-500" />
                      <span className="text-xs text-gray-700">Tenant can edit this group</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={tenantCanHide} onChange={(e) => setTenantCanHide(e.target.checked)}
                        className="w-3.5 h-3.5 text-orange-600 rounded focus:ring-orange-500" />
                      <span className="text-xs text-gray-700">Tenant can hide this group</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={isSubmitting || !code.trim() || !name.trim() || isDuplicateCode}
              className="px-3 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50">
              {isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
